import React, { useState, useEffect, useMemo, Suspense, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  LinearProgress,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Bedtime as SleepIcon,
  Water as HydrationIcon,
  Restaurant as NutritionIcon,
  DirectionsRun as RunIcon,
  FitnessCenter as StrengthIcon,
  Pool as CrossTrainingIcon,
  Schedule as TimeIcon,
  Star as PointsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckIcon,
  AccessTime as LateIcon,
  Edit as EditIcon,
  Favorite as HealthIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalHospital as SickIcon,
  Healing as InjuredIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { parseISO, format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, isWithinInterval, eachWeekOfInterval, eachMonthOfInterval } from 'date-fns';
import { DailyActivity, ActivitySubmissionResult, ActivityFormData, ActivityChartVariant } from '../../types';
import { activityService } from '../../services/activityService';
import { pointsService } from '../../services/pointsService';
import { userService } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import RunningActivityModal from './RunningActivityModal';
import { getActivityMiles, isWorkoutSplitMilesActivity } from '../../utils/activityUtils';
import type { ActivityDashboardChartVariants } from '../../types/activityChartVariant';

// Lazy load charts component to improve initial load time
const ChartsSection = React.lazy(() => import('./ChartsSection'));

interface ActivityDashboardProps {
  teamId: string;
  onLogActivity?: () => void;
  onActivitySubmitted?: () => void;
  refreshTrigger?: number; // Trigger to refresh activities list
  navigateToTeams?: () => void; // Navigate to teams page
  targetUserId?: string; // Optional: view another user's dashboard data
  readOnly?: boolean; // Optional: disable all edit/log interactions
  readOnlyHeaderText?: string; // Optional read-only helper text
}

interface ConsolidatedActivity {
  date: string;
  healthMetrics?: DailyActivity;
  runningActivity?: DailyActivity;
  allActivities: DailyActivity[];
}

const TEAM_ANALYTICS_STORAGE_KEY = 'teamAnalytics_preferences';
const ACTIVITY_DASHBOARD_CHART_STYLES_STORAGE_KEY = 'activityDashboard_chartStyles';

const ActivityDashboard: React.FC<ActivityDashboardProps> = ({
  teamId,
  onLogActivity,
  onActivitySubmitted,
  refreshTrigger,
  navigateToTeams,
  targetUserId,
  readOnly = false,
  readOnlyHeaderText,
}) => {
  const { user } = useAuth();
  const effectiveUserId = targetUserId || user?.id || '';
  const isReadOnlyView = readOnly;
  const lastSavedChartVariantsRef = useRef<string>('');
  const [activities, setActivities] = useState<DailyActivity[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingActivity, setEditingActivity] = useState<DailyActivity | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filterTab, setFilterTab] = useState<number>(0); // 0: All, 1: Health, 2: Running
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [selectedRunType, setSelectedRunType] = useState<
    'Run' | 'Workout' | 'Strength' | 'Cross Training' | 'All'
  >('All');
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<'3 weeks' | '1 month' | '90 days' | '6 months' | '1 year' | '2 years' | '3 years' | '4 years' | '5 years'>('90 days');
  const [pointsSummary, setPointsSummary] = useState<any>(null);

  const getSavedWeekStartsOn = (): 0 | 1 | 2 | 3 | 4 | 5 | 6 => {
    if (!user?.id) return 0;
    try {
      const raw = localStorage.getItem(`${TEAM_ANALYTICS_STORAGE_KEY}_${user.id}`);
      if (!raw) return 0;
      const parsed = JSON.parse(raw) as { weekStartsOn?: number };
      const v = parsed.weekStartsOn;
      return v === 0 || v === 1 || v === 2 || v === 3 || v === 4 || v === 5 || v === 6 ? v : 0;
    } catch {
      return 0;
    }
  };
  const weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = useMemo(() => {
    const remoteWeekStart = user?.weekStartsOn;
    if (
      remoteWeekStart === 0 ||
      remoteWeekStart === 1 ||
      remoteWeekStart === 2 ||
      remoteWeekStart === 3 ||
      remoteWeekStart === 4 ||
      remoteWeekStart === 5 ||
      remoteWeekStart === 6
    ) {
      return remoteWeekStart;
    }
    return getSavedWeekStartsOn();
  }, [user?.weekStartsOn, user?.id]);

  const saveChartVariantsToProfile = useCallback(async (variants: ActivityDashboardChartVariants) => {
    if (isReadOnlyView) return;
    if (!user?.id) return;
    const serialized = JSON.stringify(variants);
    if (lastSavedChartVariantsRef.current === serialized) return;
    lastSavedChartVariantsRef.current = serialized;
    try {
      await userService.updateActivityChartVariants(user.id, variants);
    } catch (err) {
      console.error('Failed to save chart style variants:', err);
      // Allow retry if save failed
      lastSavedChartVariantsRef.current = '';
    }
  }, [user?.id, isReadOnlyView]);

  const isStrengthActivity = (activity: DailyActivity): boolean =>
    activity.runType === 'Strength' || (activity.strengthMinutes ?? 0) > 0;

  // Cross training minutes only (exclude top-level Strength so it never mixes into CT charts).
  const isCrossTrainingActivity = (activity: DailyActivity): boolean => {
    if (isStrengthActivity(activity)) return false;
    return (
      activity.runType === 'Cross Training' ||
      (activity.crossTrainingMinutes || 0) > 0 ||
      !!activity.crossTrainingType
    );
  };

  const filterActivitiesByRunType = (
    activities: DailyActivity[],
    runType: 'Run' | 'Workout' | 'Strength' | 'Cross Training' | 'All'
  ): DailyActivity[] => {
    if (runType === 'All') {
      return activities.filter(
        activity =>
          !isCrossTrainingActivity(activity) &&
          !isStrengthActivity(activity) &&
          (activity.runType === 'Run' || activity.runType === 'Workout')
      );
    }
    if (runType === 'Cross Training') {
      return activities.filter(isCrossTrainingActivity);
    }
    if (runType === 'Strength') {
      return activities.filter(isStrengthActivity);
    }
    if (runType === 'Workout') {
      return activities.filter(
        a =>
          !isCrossTrainingActivity(a) &&
          (a.runType === 'Workout' || (a.runType === 'Run' && a.runSubType === 'Workout'))
      );
    }
    if (runType === 'Run') {
      return activities.filter(
        a =>
          a.runType === 'Run' &&
          a.runSubType !== 'Workout' &&
          !isCrossTrainingActivity(a)
      );
    }
    return [];
  };

  const calculateActivityValue = (activity: DailyActivity): number => {
    if (isStrengthActivity(activity)) {
      return activity.strengthMinutes ?? 0;
    }
    if (isCrossTrainingActivity(activity)) {
      return activity.crossTrainingMinutes || 0;
    }
    if (activity.runType === 'Run' || activity.runType === 'Workout') {
      return getActivityMiles(activity);
    }
    return 0;
  };

  const handleRunTypeChange = (event: any) => {
    const newRunType = event.target.value as 'Run' | 'Workout' | 'Strength' | 'Cross Training' | 'All';
    setSelectedRunType(newRunType);
  };

  // Handler for time frame change
  const handleTimeFrameChange = (event: any) => {
    const newTimeFrame = event.target.value as '3 weeks' | '1 month' | '90 days' | '6 months' | '1 year' | '2 years' | '3 years' | '4 years' | '5 years';
    setSelectedTimeFrame(newTimeFrame);
  };

  // Calculate cutoff date based on selected time frame
  const getCutoffDate = (timeFrame: string): string => {
    const today = new Date();
    const cutoff = new Date();
    
    switch (timeFrame) {
      case '3 weeks':
        cutoff.setDate(cutoff.getDate() - 21);
        break;
      case '1 month':
        cutoff.setMonth(cutoff.getMonth() - 1);
        break;
      case '90 days':
        cutoff.setDate(cutoff.getDate() - 90);
        break;
      case '6 months':
        cutoff.setMonth(cutoff.getMonth() - 6);
        break;
      case '1 year':
        cutoff.setFullYear(cutoff.getFullYear() - 1);
        break;
      case '2 years':
        cutoff.setFullYear(cutoff.getFullYear() - 2);
        break;
      case '3 years':
        cutoff.setFullYear(cutoff.getFullYear() - 3);
        break;
      case '4 years':
        cutoff.setFullYear(cutoff.getFullYear() - 4);
        break;
      case '5 years':
        cutoff.setFullYear(cutoff.getFullYear() - 5);
        break;
      default:
        cutoff.setDate(cutoff.getDate() - 90);
    }
    
    return format(cutoff, 'yyyy-MM-dd');
  };

  useEffect(() => {
    if (effectiveUserId && teamId) {
      loadActivities();
      // Add a small delay when refreshTrigger changes to ensure points are saved
      if (refreshTrigger && refreshTrigger > 0) {
        // Delay reloading points summary to ensure points transaction has completed
        setTimeout(() => {
          loadPointsSummary();
        }, 1000); // 1 second delay
      } else {
        loadPointsSummary();
      }
    }
  }, [effectiveUserId, teamId, refreshTrigger]); // Refresh when refreshTrigger changes

  // Also reload points summary when teamId becomes available
  useEffect(() => {
    if (effectiveUserId && teamId) {
      loadPointsSummary();
    }
  }, [teamId, effectiveUserId]);

  const loadPointsSummary = async () => {
    if (!effectiveUserId || !teamId) {
      console.log('ActivityDashboard: Cannot load points summary - missing user or teamId', { effectiveUserId: !!effectiveUserId, teamId });
      return;
    }
    
    try {
      console.log('ActivityDashboard: Loading points summary for user:', effectiveUserId, 'team:', teamId);
      
      // First, try to get user points directly to see if document exists
      const userPoints = await pointsService.getUserPoints(effectiveUserId, teamId);
      console.log('ActivityDashboard: User points from getUserPoints:', userPoints);
      
      if (!userPoints) {
        console.warn('ActivityDashboard: No points document found for user. Points may not have been created yet.');
        // Set a fallback so the card can still show something
        setPointsSummary({
          level: 1,
          experiencePoints: 0,
          levelDisplayString: 'Level 1',
          walletPoints: 0,
          currentStreak: 0,
          longestStreak: 0,
          rank: 0
        });
        return;
      }
      
      const summary = await pointsService.getPointsSummary(effectiveUserId, teamId);
      console.log('ActivityDashboard: Points summary loaded:', summary);
      console.log('ActivityDashboard: Experience Points:', summary.experiencePoints);
      console.log('ActivityDashboard: Level:', summary.level);
      setPointsSummary(summary);
    } catch (err) {
      console.error('ActivityDashboard: Error loading points summary:', err);
      console.error('ActivityDashboard: Error details:', err);
      // Set a fallback so the card can still show something
      setPointsSummary({
        level: 1,
        experiencePoints: 0,
        levelDisplayString: 'Level 1',
        walletPoints: 0,
        currentStreak: 0,
        longestStreak: 0,
        rank: 0
      });
    }
  };

  // Calculate XP needed for next level and progress percentage (must match pointsService thresholds)
  // Level N "ends at" (nextLevelXP - 1): e.g. Level 1 = 0-9, so bar and "XP to go" use 9 as the target.
  const calculateLevelProgress = () => {
    if (!pointsSummary) return { currentXP: 0, endOfLevelXP: 0, progressPercent: 0, xpToNextLevel: 0 };
    
    const currentXP = pointsSummary.experiencePoints || 0;
    const level = pointsSummary.level || 1;
    
    // XP thresholds: Level 1: 0-9, 2: 10-24, 3: 25-39, 4: 40-54, 5: 55-74, Prestige: 75+
    const thresholds = [0, 0, 10, 25, 40, 55, 75];
    
    if (level >= 6) {
      return { currentXP, endOfLevelXP: 75, progressPercent: 100, xpToNextLevel: 0 };
    }
    
    const currentLevelXP = thresholds[level];
    const nextThreshold = thresholds[level + 1];
    const endOfLevelXP = nextThreshold - 1; // Max XP that keeps you in this level (e.g. 9 for L1)
    const xpInCurrentLevel = currentXP - currentLevelXP;
    const xpNeededToFillLevel = endOfLevelXP - currentLevelXP;
    const progressPercent = xpNeededToFillLevel > 0 ? (xpInCurrentLevel / xpNeededToFillLevel) * 100 : 100;
    const xpToNextLevel = Math.max(0, endOfLevelXP - currentXP);
    
    return { currentXP, endOfLevelXP, progressPercent, xpToNextLevel };
  };

  const loadActivities = async () => {
    if (!effectiveUserId || !teamId) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // activityService automatically uses REST API on iOS, SDK on web/Android
      // Load enough activities to cover 2 years of data (730+ activities to be safe)
      const [activitiesData, statsData] = await Promise.all([
        activityService.getUserActivities(effectiveUserId, 1000, teamId),
        activityService.getUserActivityStats(effectiveUserId, 7, teamId)
      ]);

      setActivities(activitiesData);
      setStats(statsData);
    } catch (err: any) {
      console.error('Error loading activities:', err);
      // On error, set empty arrays so app doesn't hang
      setActivities([]);
      setStats(null);
      
      // Check if this is an authentication error (missing idToken)
      const isAuthError = err?.message?.includes('authentication token') || err?.message?.includes('sign in again');
      
      if (isAuthError) {
        setError('Authentication expired. Please sign out and sign in again.');
      } else {
        setError(err.message || 'Failed to load activity data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivitySubmitted = (result: ActivitySubmissionResult) => {
    console.log('Activity submitted:', result);
    loadActivities(); // Reload activities after submission
  };

  const handleEditActivity = (activity: DailyActivity) => {
    setEditingActivity(activity);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditingActivity(null);
    setShowEditModal(false);
  };

  const handleActivityUpdated = () => {
    loadActivities(); // Reload activities after update
    setShowEditModal(false);
    setEditingActivity(null);
    
    // Reload points summary after activity update (with delay to ensure points are saved)
    setTimeout(() => {
      loadPointsSummary();
    }, 1000);
    
    // Call onActivitySubmitted callback if provided
    if (onActivitySubmitted) {
      onActivitySubmitted();
    }
  };


  const getTimingIcon = (timing: string) => {
    switch (timing) {
      case 'same_day':
        return <CheckIcon color="success" />;
      case 'early_next':
        return <TimeIcon color="warning" />;
      case 'late_next':
        return <LateIcon color="info" />;
      case 'no_points':
        return <LateIcon color="error" />;
      default:
        return <TimeIcon />;
    }
  };

  const getTimingColor = (timing: string) => {
    switch (timing) {
      case 'same_day':
        return 'success';
      case 'early_next':
        return 'warning';
      case 'late_next':
        return 'info';
      case 'no_points':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTimingLabel = (timing: string) => {
    switch (timing) {
      case 'same_day':
        return 'Same Day';
      case 'early_next':
        return 'Early Next Day';
      case 'late_next':
        return 'Late Next Day';
      case 'no_points':
        return 'No Points';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString: string) => {
    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Determine if activity has health metrics
  const hasHealthMetrics = (activity: DailyActivity): boolean => {
    return activity.sleepScore > 0 || activity.hydrationLevel > 0 || activity.nutritionLevel > 0;
  };

  // Determine if activity has running activity (all non-None types show in list: Run, Workout, Cross Training, Rest Day, Sick, Injured)
  const hasRunningActivity = (activity: DailyActivity): boolean => {
    if (!activity.runType || activity.runType === 'None') return false;
    if (['Rest Day', 'Sick', 'Injured', 'Workout', 'Cross Training', 'Strength'].includes(activity.runType)) {
      return true;
    }
    if (activity.runType === 'Run') {
      // Volume uses getActivityMiles (Race Day = 0); still show as a run if miles or race time logged
      return (
        getActivityMiles(activity) > 0 ||
        (activity.milesRun ?? 0) > 0 ||
        (activity.raceTime != null && activity.raceTime > 0)
      );
    }
    return false;
  };

  const getActivityTypeLabel = (activity: DailyActivity): string => {
    if (activity.runType === 'Run' && activity.runSubType && activity.runSubType !== 'Workout') {
      return `Run ${activity.runSubType}`;
    }
    if (isWorkoutSplitMilesActivity(activity)) {
      const w = activity.workoutType;
      if (w === 'VO2 Max' || w === 'Hard workout' || w === 'Hard') return 'Hard';
      if (w) return w;
      return 'Workout';
    }
    if (activity.runType === 'Cross Training') {
      return activity.crossTrainingType || 'Cross Training';
    }
    if (activity.runType === 'Strength') {
      return 'Strength';
    }
    if (activity.runType === 'Sick') return 'Sick';
    if (activity.runType === 'Injured') return 'Injured';
    if (activity.runType === 'Rest Day') return 'Rest';
    if (activity.runType && activity.runType !== 'None') return activity.runType;
    return '';
  };

  const getActivityTypeColor = (activity: DailyActivity): string => {
    const label = getActivityTypeLabel(activity);
    const colors: Record<string, string> = {
      'Run Easy': '#39FF14',
      'Run Long': '#89CFF0',
      'Run Race Day': '#FFD700',
      Tempo: '#FFD700',
      Threshold: '#FF8C00',
      Hard: '#FF4444',
      'Hard Workout': '#FF4444',
      'VO2 Max': '#FF4444',
      Workout: '#FF8C00',
      'Cross Training': '#FF00FF',
      Strength: '#9C27B0',
      Rest: '#808080',
      'Rest Day': '#808080',
      Sick: '#FFB6C1',
      Injured: '#FF6347',
    };
    return colors[label] || '#999999';
  };

  // Consolidate activities by date
  const consolidatedActivities = useMemo(() => {
    const grouped = new Map<string, ConsolidatedActivity>();
    
    activities.forEach(activity => {
      if (!grouped.has(activity.date)) {
        grouped.set(activity.date, {
          date: activity.date,
          allActivities: [],
        });
      }
      
      const consolidated = grouped.get(activity.date)!;
      consolidated.allActivities.push(activity);
      
      if (hasHealthMetrics(activity) && !consolidated.healthMetrics) {
        consolidated.healthMetrics = activity;
      }
      
      if (hasRunningActivity(activity) && !consolidated.runningActivity) {
        consolidated.runningActivity = activity;
      }
    });
    
    return Array.from(grouped.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [activities]);

  // Filter consolidated activities based on tab (tab index: 0 All, 1 Health, 2 Running)
  const filteredActivities = useMemo(() => {
    const tab = Number(filterTab);
    if (tab === 0) return consolidatedActivities; // All
    if (tab === 1) return consolidatedActivities.filter(a => a.healthMetrics && hasHealthMetrics(a.healthMetrics)); // Health only
    return consolidatedActivities.filter(a => a.runningActivity && hasRunningActivity(a.runningActivity)); // Running only
  }, [consolidatedActivities, filterTab]);

  // Auto-expand most recent entry
  useEffect(() => {
    if (consolidatedActivities.length > 0 && expandedDates.size === 0) {
      setExpandedDates(new Set([consolidatedActivities[0].date]));
    }
  }, [consolidatedActivities, expandedDates.size]);

  // Toggle expanded state
  const toggleExpanded = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  /** One logged record: health and/or running UI (All tab, multiple entries per day). */
  const renderHealthMetricsBlock = (act: DailyActivity, addBottomMargin: boolean) => (
    <Box
      key={`health-${act.id}`}
      sx={{
        mb: addBottomMargin ? 2 : 0,
        p: 2,
        bgcolor: 'rgba(25, 118, 210, 0.08)',
        borderRadius: 1,
        borderLeft: '3px solid',
        borderColor: 'primary.main',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <HealthIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={600} color="primary">
          Health Metrics
        </Typography>
        {!isReadOnlyView && (
          <Box sx={{ ml: 'auto' }}>
            <Button size="small" onClick={() => handleEditActivity(act)} startIcon={<EditIcon />} color="primary">
              Edit
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {act.sleepScore > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SleepIcon fontSize="small" color="primary" />
            <Typography variant="body2">
              Sleep: <strong>{act.sleepScore}/10</strong>
            </Typography>
          </Box>
        )}
        {act.hydrationLevel > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <HydrationIcon fontSize="small" color="info" />
            <Typography variant="body2">
              Hydration: <strong>{act.hydrationLevel}/10</strong>
            </Typography>
          </Box>
        )}
        {act.nutritionLevel > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <NutritionIcon fontSize="small" color="success" />
            <Typography variant="body2">
              Nutrition: <strong>{act.nutritionLevel}/10</strong>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderRunningActivityBlock = (act: DailyActivity) => {
    const workoutMiles = isWorkoutSplitMilesActivity(act) ? getActivityMiles(act) : 0;
    const label = getActivityTypeLabel(act);
    const color = getActivityTypeColor(act);
    return (
      <Box
        key={`run-${act.id}`}
        sx={{
          p: 2,
          bgcolor: 'rgba(46, 125, 50, 0.08)',
          borderRadius: 1,
          borderLeft: '3px solid',
          borderColor: 'success.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {act.runType === 'Sick' ? (
            <SickIcon color="error" fontSize="small" />
          ) : act.runType === 'Injured' ? (
            <InjuredIcon color="error" fontSize="small" />
          ) : (
            <RunIcon color="success" fontSize="small" />
          )}
          <Typography variant="subtitle2" fontWeight={600} color="success.dark">
            Activity
          </Typography>
          {!isReadOnlyView && (
            <Box sx={{ ml: 'auto' }}>
              <Button size="small" onClick={() => handleEditActivity(act)} startIcon={<EditIcon />} color="success">
                Edit
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip label={label || act.runType} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 600 }} />
          {act.milesRun > 0 && (
            <Typography variant="body2">
              <strong>{act.milesRun.toFixed(2)} miles</strong>
            </Typography>
          )}
          {workoutMiles > 0 && (
            <Typography variant="body2">
              <strong>{workoutMiles.toFixed(2)} miles</strong>
            </Typography>
          )}
          {act.crossTrainingMinutes > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CrossTrainingIcon fontSize="small" color="info" />
              <Typography variant="body2">
                <strong>{act.crossTrainingMinutes} min</strong> (cross training)
              </Typography>
            </Box>
          )}
          {(act.strengthMinutes ?? 0) > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StrengthIcon fontSize="small" color="secondary" />
              <Typography variant="body2">
                <strong>{act.strengthMinutes} min</strong> (strength)
              </Typography>
            </Box>
          )}
        </Box>
        {isWorkoutSplitMilesActivity(act) && (
          <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
            {(act.warmupMiles || 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                Warmup: {act.warmupMiles} mi
              </Typography>
            )}
            {(act.workoutMiles || 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                Workout: {act.workoutMiles} mi
              </Typography>
            )}
            {(act.cooldownMiles || 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                Cooldown: {act.cooldownMiles} mi
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Prepare cumulative miles data for chart (limit by selected time frame for performance)
  const cumulativeMilesData = useMemo(() => {
    if (activities.length === 0) return [];
    
    // Filter by run type first
    const filteredByRunType = filterActivitiesByRunType(activities, selectedRunType);
    
    // Debug: Log what activities are being filtered
    if (selectedRunType === 'All') {
      const crossTrainingActivities = activities.filter(isCrossTrainingActivity);
      console.log('[Cumulative Miles Chart] All activities:', activities.length);
      console.log('[Cumulative Miles Chart] Cross Training activities found:', crossTrainingActivities.length, crossTrainingActivities.map(a => ({ date: a.date, runType: a.runType, milesRun: a.milesRun, crossTrainingMinutes: a.crossTrainingMinutes })));
      console.log('[Cumulative Miles Chart] After filter (should exclude CT):', filteredByRunType.length, filteredByRunType.map(a => ({ date: a.date, runType: a.runType, milesRun: a.milesRun })));
    }
    
    // Calculate cutoff date based on selected time frame
    const cutoffDate = getCutoffDate(selectedTimeFrame);
    
    // Filter by date and calculate activity values
    // CRITICAL: When showing "All", we must ONLY include Run and Workout activities (miles-based).
    // Cross Training uses minutes and must NEVER appear in cumulative miles calculations.
    const activitiesWithValues = filteredByRunType
      .filter(a => {
        // Safety check: explicitly exclude Cross Training activities (defensive programming)
        if (selectedRunType === 'All' && isCrossTrainingActivity(a)) {
          console.warn('[Cumulative Miles Chart] ERROR: Cross Training activity found in "All" filter!', {
            date: a.date,
            runType: a.runType,
            milesRun: a.milesRun,
            crossTrainingMinutes: a.crossTrainingMinutes
          });
          return false;
        }
        if (selectedRunType === 'All' && isStrengthActivity(a)) {
          return false;
        }
        if (selectedRunType === 'All' && a.runType !== 'Run' && a.runType !== 'Workout') {
          return false;
        }
        const value = calculateActivityValue(a);
        return value > 0 && a.date >= cutoffDate;
      })
      .map(a => ({
        activity: a,
        value: calculateActivityValue(a),
        date: a.date
      }));
    
    if (activitiesWithValues.length === 0) return [];
    
    // Group by date and sum values for same date
    const groupedByDate = new Map<string, number>();
    activitiesWithValues.forEach(({ date, value }) => {
      const current = groupedByDate.get(date) || 0;
      groupedByDate.set(date, current + value);
    });
    
    // Convert to array and sort by date
    const sortedByDate = Array.from(groupedByDate.entries())
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date));
    
    if (sortedByDate.length === 0) return [];
    
    let cumulative = 0;
    // Limit to max 100 data points for performance
    const step = Math.max(1, Math.ceil(sortedByDate.length / 100));
    const limitedData = sortedByDate.filter((_, index) => index % step === 0 || index === sortedByDate.length - 1);
    
    return limitedData.map(({ date, value }) => {
      cumulative += value;
      return {
        date: format(parseISO(date), 'MMM d'),
        fullDate: date,
        miles: value,
        cumulative: parseFloat(cumulative.toFixed(2)),
        isCrossTraining: selectedRunType === 'Cross Training' || selectedRunType === 'Strength'
      };
    });
  }, [activities, selectedRunType, selectedTimeFrame]);

  // Prepare health metrics data for chart (limit by selected time frame for performance)
  // Prepare race day activities data for chart markers
  const raceDayActivities = useMemo(() => {
    if (activities.length === 0) return [];
    
    // Calculate cutoff date based on selected time frame
    const cutoffDate = getCutoffDate(selectedTimeFrame);
    
    // Filter for Race Day activities with race times
    return activities
      .filter(a => 
        a.runSubType === 'Race Day' && 
        a.raceTime && 
        a.date >= cutoffDate
      )
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(activity => ({
        date: format(parseISO(activity.date), 'MMM d'),
        fullDate: activity.date,
        eventType: activity.raceEventType || 'Race',
        raceTime: activity.raceTime || 0,
        miles: activity.milesRun,
      }));
  }, [activities, selectedTimeFrame]);

  const healthMetricsData = useMemo(() => {
    if (activities.length === 0) return [];
    
    // Calculate cutoff date based on selected time frame
    const cutoffDate = getCutoffDate(selectedTimeFrame);
    
    // Filter activities by date first
    const filteredActivities = activities.filter(a => a.date >= cutoffDate && hasHealthMetrics(a));
    
    if (filteredActivities.length === 0) return [];
    
    // Group by date and calculate averages
    const grouped = new Map<string, { sleep: number[]; hydration: number[]; nutrition: number[] }>();
    
    filteredActivities.forEach(activity => {
      if (!grouped.has(activity.date)) {
        grouped.set(activity.date, { sleep: [], hydration: [], nutrition: [] });
      }
      const data = grouped.get(activity.date)!;
      if (activity.sleepScore > 0) data.sleep.push(activity.sleepScore);
      if (activity.hydrationLevel > 0) data.hydration.push(activity.hydrationLevel);
      if (activity.nutritionLevel > 0) data.nutrition.push(activity.nutritionLevel);
    });
    
    // Convert to array and sort by date
    const result = Array.from(grouped.entries())
      .map(([date, scores]) => ({
        date: format(parseISO(date), 'MMM d'),
        fullDate: date,
        sleep: scores.sleep.length > 0 
          ? parseFloat((scores.sleep.reduce((sum, s) => sum + s, 0) / scores.sleep.length).toFixed(1))
          : 0,
        hydration: scores.hydration.length > 0
          ? parseFloat((scores.hydration.reduce((sum, h) => sum + h, 0) / scores.hydration.length).toFixed(1))
          : 0,
        nutrition: scores.nutrition.length > 0
          ? parseFloat((scores.nutrition.reduce((sum, n) => sum + n, 0) / scores.nutrition.length).toFixed(1))
          : 0,
      }))
      .sort((a, b) => a.fullDate.localeCompare(b.fullDate));
    
    // Limit to max 100 data points for performance
    if (result.length > 100) {
      const step = Math.ceil(result.length / 100);
      return result.filter((_, index) => index % step === 0 || index === result.length - 1);
    }
    
    return result;
  }, [activities, selectedTimeFrame]);

  // Calculate weekly miles totals for last 3 months (week-by-week breakdown)
  const weeklyMilesData = useMemo(() => {
    if (activities.length === 0) return [];
    
    // Get activities that contribute to miles (Run and Workout only, exclude Cross Training)
    const milesActivities = activities.filter(a => {
      if (isCrossTrainingActivity(a) || isStrengthActivity(a)) return false;
      return a.runType === 'Run' || a.runType === 'Workout';
    });
    
    if (milesActivities.length === 0) return [];
    
    // Calculate 3 months ago date
    const today = new Date();
    const threeMonthsAgo = subMonths(today, 3);
    const startDate = startOfWeek(threeMonthsAgo, { weekStartsOn });
    const endDate = endOfWeek(today, { weekStartsOn });
    
    // Generate all weeks in the range
    const weeks = eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn });
    
    // Calculate miles for each week
    const weeklyData = weeks.map(weekStart => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn });
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
      
      // Sum miles for activities in this week
      const weekMiles = milesActivities
        .filter(a => {
          const activityDate = parseISO(a.date);
          return isWithinInterval(activityDate, { start: weekStart, end: weekEnd });
        })
        .reduce((sum, a) => sum + getActivityMiles(a), 0);
      
      return {
        weekLabel: format(weekStart, 'MMM d'),
        weekStart: weekStartStr,
        weekEnd: weekEndStr,
        miles: parseFloat(weekMiles.toFixed(2)),
        fullDate: weekStartStr // For sorting
      };
    }).filter(week => week.miles > 0); // Only include weeks with miles
    
    return weeklyData.sort((a, b) => a.fullDate.localeCompare(b.fullDate));
  }, [activities, weekStartsOn]);

  // Calculate monthly miles totals for last 2 years (month-by-month breakdown)
  const monthlyMilesData = useMemo(() => {
    if (activities.length === 0) return [];
    
    // Get activities that contribute to miles (Run and Workout only, exclude Cross Training)
    const milesActivities = activities.filter(a => {
      if (isCrossTrainingActivity(a) || isStrengthActivity(a)) return false;
      return a.runType === 'Run' || a.runType === 'Workout';
    });
    
    if (milesActivities.length === 0) return [];
    
    // Calculate 2 years ago date
    const today = new Date();
    const twoYearsAgo = subMonths(today, 24);
    const startDate = startOfMonth(twoYearsAgo);
    const endDate = endOfMonth(today);
    
    // Generate all months in the range
    const months = eachMonthOfInterval({ start: startDate, end: endDate });
    
    // Calculate miles for each month
    const monthlyData = months.map(monthStart => {
      const monthEnd = endOfMonth(monthStart);
      const monthStartStr = format(monthStart, 'yyyy-MM-dd');
      const monthEndStr = format(monthEnd, 'yyyy-MM-dd');
      
      // Sum miles for activities in this month
      const monthMiles = milesActivities
        .filter(a => {
          const activityDate = parseISO(a.date);
          return isWithinInterval(activityDate, { start: monthStart, end: monthEnd });
        })
        .reduce((sum, a) => sum + getActivityMiles(a), 0);
      
      return {
        monthLabel: format(monthStart, 'MMM yyyy'),
        monthStart: monthStartStr,
        monthEnd: monthEndStr,
        miles: parseFloat(monthMiles.toFixed(2)),
        fullDate: monthStartStr // For sorting
      };
    }).filter(month => month.miles > 0); // Only include months with miles
    
    return monthlyData.sort((a, b) => a.fullDate.localeCompare(b.fullDate));
  }, [activities]);

  // Get health metrics trend indicator
  const getHealthTrend = (current: DailyActivity | undefined, previous: DailyActivity | undefined) => {
    if (!current || !previous || !hasHealthMetrics(current) || !hasHealthMetrics(previous)) {
      return null;
    }
    
    const currentAvg = (current.sleepScore + current.hydrationLevel + current.nutritionLevel) / 3;
    const previousAvg = (previous.sleepScore + previous.hydrationLevel + previous.nutritionLevel) / 3;
    
    if (currentAvg > previousAvg + 0.5) {
      return <TrendingUpIcon fontSize="small" color="success" />;
    } else if (currentAvg < previousAvg - 0.5) {
      return <TrendingDownIcon fontSize="small" color="error" />;
    }
    return null;
  };

  // Check if teamId is valid (after all hooks)
  if (!teamId || teamId.trim() === '') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="warning"
          action={
            navigateToTeams && (
              <Button 
                color="inherit" 
                size="small" 
                variant="outlined"
                onClick={navigateToTeams}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {(user?.role === 'coach' || user?.role === 'headCoach') ? 'Create Team' : 'Join Team'}
              </Button>
            )
          }
        >
          You need to be associated with a team to view daily activities. 
          {(user?.role === 'coach' || user?.role === 'headCoach') ? ' Please create a team first.' : ' Please join a team first.'}
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h4">
                Activity Dashboard
              </Typography>
              {isReadOnlyView && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {readOnlyHeaderText || 'Read-only view. Logging and editing are disabled.'}
                </Typography>
              )}
              {(user?.role === 'coach' || user?.role === 'headCoach') && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Click "Log Today's Activity" to log your daily health metrics and running activities
                </Typography>
              )}
            </Box>
            {!isReadOnlyView && onLogActivity && (
              <Button
                variant="contained"
                onClick={onLogActivity}
                size="large"
              >
                Log Activity
              </Button>
            )}
          </Box>

      {/* Level Progress Display - Always show if user is logged in */}
      {user && (
        <Box sx={{ mb: 3, px: { xs: 1, sm: 2 }, display: 'block' }}>
          <Card sx={{ background: '#1976d2', color: 'white', display: 'block' }}>
            <CardContent sx={{ py: 2, display: 'block' }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                justifyContent: 'space-between', 
                gap: 2 
              }}>
                {/* Level Display */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                  <TrophyIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {pointsSummary?.levelDisplayString || `Level ${pointsSummary?.level || 1}`}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {pointsSummary?.experiencePoints || 0} XP
                    </Typography>
                  </Box>
                </Box>

                {/* XP Progress Bar */}
                <Box sx={{ 
                  width: { xs: '100%', sm: 'auto' },
                  flex: { xs: 'none', sm: 1 },
                  minWidth: { xs: 0, sm: 200 },
                  maxWidth: { xs: '100%', sm: 500 }
                }}>
                  {(() => {
                    const { currentXP, endOfLevelXP, progressPercent, xpToNextLevel } = pointsSummary ? calculateLevelProgress() : { currentXP: 0, endOfLevelXP: 9, progressPercent: 0, xpToNextLevel: 9 };
                    const isPrestige = pointsSummary ? pointsSummary.level >= 6 : false;
                    
                    return (
                      <Box sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.15)', 
                        borderRadius: 2, 
                        p: { xs: 1.5, sm: 2 },
                        border: '1px solid rgba(255,255,255,0.2)',
                        width: '100%'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between', 
                          mb: 1,
                          gap: { xs: 0.5, sm: 0 }
                        }}>
                          <Typography variant="body2" sx={{ opacity: 1, fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {isPrestige ? 'Prestige Achieved!' : 'Progress to Next Level'}
                          </Typography>
                          {!isPrestige && (
                            <Typography variant="body2" sx={{ opacity: 1, fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {xpToNextLevel.toFixed(1)} XP to go
                            </Typography>
                          )}
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(progressPercent, 100)} 
                          sx={{ 
                            height: { xs: 12, sm: 14 }, 
                            borderRadius: { xs: 6, sm: 7 },
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            width: '100%',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: isPrestige ? '#FFD700' : '#4caf50',
                              borderRadius: { xs: 6, sm: 7 },
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }
                          }} 
                        />
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          mt: 1,
                          gap: 1
                        }}>
                          <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {currentXP.toFixed(1)} XP
                          </Typography>
                          {!isPrestige && (
                            <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {endOfLevelXP} XP
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Charts Section - Lazy loaded to improve initial page load */}
      {!loading && (cumulativeMilesData.length > 0 || healthMetricsData.length > 0 || selectedRunType !== 'All') && (
        <Box sx={{ mb: 3 }}>
          {/* Activity Type Dropdown - Always show when charts section is visible */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', pl: '5px' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="activity-type-select-label">Activity Type</InputLabel>
              <Select
                labelId="activity-type-select-label"
                id="activity-type-select"
                value={selectedRunType}
                label="Activity Type"
                onChange={handleRunTypeChange}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Run">Runs</MenuItem>
                <MenuItem value="Workout">Workouts</MenuItem>
                <MenuItem value="Strength">Strength</MenuItem>
                <MenuItem value="Cross Training">Cross Training</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Time Frame Dropdown */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', pl: '5px' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="time-frame-select-label">Time Frame</InputLabel>
              <Select
                labelId="time-frame-select-label"
                id="time-frame-select"
                value={selectedTimeFrame}
                label="Time Frame"
                onChange={handleTimeFrameChange}
              >
                <MenuItem value="3 weeks">Last 3 Weeks</MenuItem>
                <MenuItem value="1 month">1 Month</MenuItem>
                <MenuItem value="90 days">90 Days</MenuItem>
                <MenuItem value="6 months">6 Months</MenuItem>
                <MenuItem value="1 year">1 Year</MenuItem>
                <MenuItem value="2 years">2 Years</MenuItem>
                <MenuItem value="3 years">3 Years</MenuItem>
                <MenuItem value="4 years">4 Years</MenuItem>
                <MenuItem value="5 years">5 Years</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Empty State for Filtered View */}
          {cumulativeMilesData.length === 0 && selectedRunType !== 'All' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No {selectedRunType} activities found in the last {selectedTimeFrame}. Log your first {selectedRunType.toLowerCase()} activity to see it here!
            </Alert>
          )}

          {/* Only show charts if there's data, or if showing "All" (which might have health metrics) */}
          {(cumulativeMilesData.length > 0 || healthMetricsData.length > 0) && (
            <Suspense fallback={
              <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            }>
              <ChartsSection
                cumulativeMilesData={cumulativeMilesData}
                healthMetricsData={healthMetricsData}
                weeklyMilesData={weeklyMilesData}
                monthlyMilesData={monthlyMilesData}
                selectedRunType={selectedRunType}
                raceDayActivities={raceDayActivities}
                initialChartVariant={user?.activityChartVariant as ActivityChartVariant | undefined}
                initialChartVariants={user?.activityChartVariants}
                weekStartsOn={weekStartsOn}
                chartPreferencesStorageKey={user?.id ? `${ACTIVITY_DASHBOARD_CHART_STYLES_STORAGE_KEY}_${user.id}` : undefined}
                onChartVariantsChange={saveChartVariantsToProfile}
              />
            </Suspense>
          )}
        </Box>
      )}

      {/* Stats Overview */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SleepIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Avg Sleep</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {stats.averageSleepScore}/10
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <HydrationIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6">Avg Hydration</Typography>
                </Box>
                <Typography variant="h4" color="info.main">
                  {stats.averageHydrationLevel}/10
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NutritionIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">Avg Nutrition</Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {stats.averageNutritionLevel}/10
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PointsIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="h6">Total Points</Typography>
                </Box>
                <Typography variant="h4" color="warning.main">
                  {stats.totalPointsEarned}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Activity History */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Activity History ({filteredActivities.length} days)
            </Typography>
          </Box>

          {/* Filter Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={filterTab}
              onChange={(_, newValue) => setFilterTab(Number(newValue))}
            >
              <Tab label="All" value={0} />
              <Tab label="Health Metrics" value={1} />
              <Tab label="Running Activities" value={2} />
            </Tabs>
          </Box>

          {filteredActivities.length === 0 ? (
            <Alert severity="info">
              {filterTab === 1 
                ? 'No health metrics found. Start logging your daily health metrics!'
                : filterTab === 2
                ? 'No running activities found. Start logging your running activities!'
                : 'No activity logs found. Start logging your daily activities to see your progress here!'}
            </Alert>
          ) : (
            <Box>
              {filteredActivities.map((consolidated, index) => {
                const isExpanded = expandedDates.has(consolidated.date);
                const hasBoth = consolidated.healthMetrics && consolidated.runningActivity;
                const tab = Number(filterTab);
                const showHealth = tab === 0 || tab === 1;
                const showRunning = tab === 0 || tab === 2;
                const previousActivity = index < filteredActivities.length - 1 
                  ? filteredActivities[index + 1]
                  : null;
                const trend = getHealthTrend(consolidated.healthMetrics, previousActivity?.healthMetrics);

                return (
                  <React.Fragment key={consolidated.date}>
                    {/* Date Header */}
                    <Card 
                      sx={{ 
                        mb: 2,
                        borderLeft: '4px solid',
                        borderColor: hasBoth ? 'purple' : consolidated.healthMetrics ? 'primary.main' : 'success.main',
                        boxShadow: 2
                      }}
                    >
                      <CardContent 
                        sx={{ pb: 1, cursor: 'pointer' }}
                        onClick={() => toggleExpanded(consolidated.date)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {formatDate(consolidated.date)}
                            </Typography>
                            
                            {/* Entry Type Badges - only show for current tab on filtered views */}
                            {showHealth && consolidated.healthMetrics && (
                              <Chip
                                icon={<HealthIcon />}
                                label="Health Metrics"
                                size="small"
                                color="primary"
                                variant={hasBoth ? "outlined" : "filled"}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                            {showRunning && consolidated.runningActivity && (
                              <Chip
                                icon={<RunIcon />}
                                label="Activity"
                                size="small"
                                color="success"
                                variant={hasBoth ? "outlined" : "filled"}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                            {tab === 0 && hasBoth && (
                              <Chip
                                label="Complete Entry"
                                size="small"
                                sx={{ 
                                  bgcolor: 'purple',
                                  color: 'white',
                                  fontWeight: 600
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                            
                            {/* Timing and Points */}
                            {consolidated.allActivities[0] && (
                              <>
                                <Chip
                                  label={getTimingLabel(consolidated.allActivities[0].submissionTiming)}
                                  color={getTimingColor(consolidated.allActivities[0].submissionTiming) as any}
                                  size="small"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                {consolidated.allActivities.some(a => a.pointsEarned > 0) && (
                                  <Chip
                                    icon={<PointsIcon />}
                                    label={`+${consolidated.allActivities.reduce((sum, a) => sum + (a.pointsEarned || 0), 0)} pts`}
                                    color="warning"
                                    size="small"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                              </>
                            )}
                            
                            {/* Trend Indicator */}
                            {trend}
                          </Box>
                          
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(consolidated.date);
                            }}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                      </CardContent>
                      
                      {/* Collapsed Summary - only show section(s) for current tab */}
                      {!isExpanded && (
                        <CardContent sx={{ pt: 0 }}>
                          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                            {showHealth && consolidated.healthMetrics && hasHealthMetrics(consolidated.healthMetrics) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HealthIcon fontSize="small" color="primary" />
                                <Typography variant="body2" color="text.secondary">
                                  {[
                                    consolidated.healthMetrics.sleepScore > 0 && `Sleep: ${consolidated.healthMetrics.sleepScore}/10`,
                                    consolidated.healthMetrics.hydrationLevel > 0 && `Hydration: ${consolidated.healthMetrics.hydrationLevel}/10`,
                                    consolidated.healthMetrics.nutritionLevel > 0 && `Nutrition: ${consolidated.healthMetrics.nutritionLevel}/10`
                                  ].filter(Boolean).join(' • ')}
                                </Typography>
                              </Box>
                            )}
                            {showRunning && consolidated.runningActivity && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {consolidated.runningActivity.runType === 'Sick' ? (
                                  <SickIcon fontSize="small" color="error" />
                                ) : consolidated.runningActivity.runType === 'Injured' ? (
                                  <InjuredIcon fontSize="small" color="error" />
                                ) : (
                                  <RunIcon fontSize="small" color="success" />
                                )}
                                <Typography variant="body2" color="text.secondary">
                                  {getActivityTypeLabel(consolidated.runningActivity) || consolidated.runningActivity.runType}
                                  {isWorkoutSplitMilesActivity(consolidated.runningActivity) && (() => {
                                    const total = getActivityMiles(consolidated.runningActivity!);
                                    return total > 0 ? ` • ${total.toFixed(1)} mi` : '';
                                  })()}
                                  {consolidated.runningActivity.milesRun > 0 && consolidated.runningActivity.runSubType !== 'Workout' && ` • ${consolidated.runningActivity.milesRun.toFixed(1)} mi`}
                                  {consolidated.runningActivity.crossTrainingMinutes > 0 && ` • ${consolidated.runningActivity.crossTrainingMinutes} min CT`}
                                  {(consolidated.runningActivity.strengthMinutes ?? 0) > 0 && ` • ${consolidated.runningActivity.strengthMinutes} min strength`}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      )}
                      
                      {/* Collapsible Content - only show section(s) for current tab */}
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <CardContent sx={{ pt: 0 }}>
                          {/* All tab + multiple logs same day: list every entry */}
                          {tab === 0 && consolidated.allActivities.length > 1 ? (
                            (() => {
                              const acts = consolidated.allActivities;
                              const healthPrimaryIdx = acts.findIndex(
                                a => hasHealthMetrics(a) && !hasRunningActivity(a)
                              );
                              const healthOnceIdx =
                                healthPrimaryIdx >= 0
                                  ? healthPrimaryIdx
                                  : acts.findIndex(a => hasHealthMetrics(a));
                              return acts.map((act, entryIdx) => {
                              const showH =
                                hasHealthMetrics(act) &&
                                healthOnceIdx >= 0 &&
                                entryIdx === healthOnceIdx;
                              const showR = hasRunningActivity(act);
                              const lastEntry = entryIdx === acts.length - 1;
                              return (
                                <Box
                                  key={act.id}
                                  sx={{
                                    mb: lastEntry ? 0 : 2,
                                    pb: lastEntry ? 0 : 2,
                                    borderBottom: lastEntry ? 'none' : '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {showH && (
                                    <Box sx={{ mb: showR ? 2 : 0 }}>{renderHealthMetricsBlock(act, false)}</Box>
                                  )}
                                  {showR && renderRunningActivityBlock(act)}
                                  {!showH && !showR && (
                                    <Typography variant="body2" color="text.secondary">
                                      {isReadOnlyView ? 'No health or activity data on this log.' : 'No health or activity data on this log (edit to add).'}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            });
                            })()
                          ) : (
                            <>
                              {/* Health Metrics Section */}
                              {showHealth && consolidated.healthMetrics && (
                                <Box
                                  sx={{
                                    mb: (showRunning && consolidated.runningActivity) ? 2 : 0,
                                    p: 2,
                                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                                    borderRadius: 1,
                                    borderLeft: '3px solid',
                                    borderColor: 'primary.main',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <HealthIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight={600} color="primary">
                                      Health Metrics
                                    </Typography>
                                    {!isReadOnlyView && (
                                      <Box sx={{ ml: 'auto' }}>
                                        <Button
                                          size="small"
                                          onClick={() => handleEditActivity(consolidated.healthMetrics!)}
                                          startIcon={<EditIcon />}
                                          color="primary"
                                        >
                                          Edit
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {consolidated.healthMetrics.sleepScore > 0 && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <SleepIcon fontSize="small" color="primary" />
                                        <Typography variant="body2">
                                          Sleep: <strong>{consolidated.healthMetrics.sleepScore}/10</strong>
                                        </Typography>
                                      </Box>
                                    )}
                                    {consolidated.healthMetrics.hydrationLevel > 0 && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <HydrationIcon fontSize="small" color="info" />
                                        <Typography variant="body2">
                                          Hydration: <strong>{consolidated.healthMetrics.hydrationLevel}/10</strong>
                                        </Typography>
                                      </Box>
                                    )}
                                    {consolidated.healthMetrics.nutritionLevel > 0 && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <NutritionIcon fontSize="small" color="success" />
                                        <Typography variant="body2">
                                          Nutrition: <strong>{consolidated.healthMetrics.nutritionLevel}/10</strong>
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              )}

                              {/* Running Activity Section */}
                              {showRunning && consolidated.runningActivity && (
                                <Box
                                  sx={{
                                    p: 2,
                                    bgcolor: 'rgba(46, 125, 50, 0.08)',
                                    borderRadius: 1,
                                    borderLeft: '3px solid',
                                    borderColor: 'success.main',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    {consolidated.runningActivity.runType === 'Sick' ? (
                                      <SickIcon color="error" fontSize="small" />
                                    ) : consolidated.runningActivity.runType === 'Injured' ? (
                                      <InjuredIcon color="error" fontSize="small" />
                                    ) : (
                                      <RunIcon color="success" fontSize="small" />
                                    )}
                                    <Typography variant="subtitle2" fontWeight={600} color="success.dark">
                                      Activity
                                    </Typography>
                                    {!isReadOnlyView && (
                                      <Box sx={{ ml: 'auto' }}>
                                        <Button
                                          size="small"
                                          onClick={() => handleEditActivity(consolidated.runningActivity!)}
                                          startIcon={<EditIcon />}
                                          color="success"
                                        >
                                          Edit
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>
                                  {(() => {
                                    const label = getActivityTypeLabel(consolidated.runningActivity!);
                                    const color = getActivityTypeColor(consolidated.runningActivity!);
                                    const act = consolidated.runningActivity!;
                                    const workoutMiles = isWorkoutSplitMilesActivity(act) ? getActivityMiles(act) : 0;
                                    return (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Chip
                                          label={label || act.runType}
                                          size="small"
                                          sx={{ bgcolor: color, color: '#fff', fontWeight: 600 }}
                                        />
                                        {act.milesRun > 0 && act.runSubType !== 'Workout' && (
                                          <Typography variant="body2">
                                            <strong>{act.milesRun.toFixed(2)} miles</strong>
                                          </Typography>
                                        )}
                                        {workoutMiles > 0 && (
                                          <Typography variant="body2">
                                            <strong>{workoutMiles.toFixed(2)} miles</strong>
                                          </Typography>
                                        )}
                                        {act.crossTrainingMinutes > 0 && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CrossTrainingIcon fontSize="small" color="info" />
                                            <Typography variant="body2">
                                              <strong>{act.crossTrainingMinutes} min</strong> CT
                                            </Typography>
                                          </Box>
                                        )}
                                        {(act.strengthMinutes ?? 0) > 0 && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <StrengthIcon fontSize="small" color="secondary" />
                                            <Typography variant="body2">
                                              <strong>{act.strengthMinutes} min</strong> strength
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })()}
                                  {isWorkoutSplitMilesActivity(consolidated.runningActivity!) && (
                                    <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                                      {(consolidated.runningActivity!.warmupMiles || 0) > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                          Warmup: {consolidated.runningActivity!.warmupMiles} mi
                                        </Typography>
                                      )}
                                      {(consolidated.runningActivity!.workoutMiles || 0) > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                          Workout: {consolidated.runningActivity!.workoutMiles} mi
                                        </Typography>
                                      )}
                                      {(consolidated.runningActivity!.cooldownMiles || 0) > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                          Cooldown: {consolidated.runningActivity!.cooldownMiles} mi
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </>
                          )}
                        </CardContent>
                      </Collapse>
                    </Card>
                  </React.Fragment>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

        {/* Edit Activity Modal */}
        {!isReadOnlyView && editingActivity && (
          <RunningActivityModal
            open={showEditModal}
            onClose={handleCloseEditModal}
            onActivitySubmitted={handleActivityUpdated}
            teamId={teamId}
            editingActivity={editingActivity}
          />
        )}
    </Box>
  );
};

export default ActivityDashboard;
